import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useTranslation } from 'react-i18next';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor, orderBy } from '@progress/kendo-data-query';
import { GridSelectionChangeEvent } from '@progress/kendo-react-grid';
import ToggleAssociatedRecordsIcon from '@components-clean/core/grid/associated-records-icon';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import { GridMenuItemProps } from '@components-clean/core/grid/grid-menu/grid-menu-item';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import { ControlsHubPanelsAssociatedToRisk } from '@interfaces/central-monitoring';
import {
    CmControlsContext,
    IExtendedControlsHubPanelsAssociatedToRisk,
} from '@pages-clean/risk-assessment/cm-controls/interfaces/context';
import RiskAssessmentService from '@pages-clean/risk-assessment/risk-assessment.service';
import UnassignCmControlRiskDialog from '@pages-clean/risk-assessment/risk-log/dialogs/unassign-cm-control-risk-dialog';
import { statusRAGChips } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/helper';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { getIsActiveField, stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import { ShowError, ShowSuccess } from '@utils/notification';
import { AllPaths } from '@utils/router/all-paths';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

const CmControlChartsGrid = () => {
    const user = useAuthorization();
    const canView = user?.can?.({
        module: Constants.Modules.RAM,
        page: Constants.PageElements.CMControls,
        permission: Permissions.Enabled,
        element: Constants.PageElements.CMControls,
    });

    if (!canView) return;

    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const { current, setSelectionState } = useContext(CmControlsContext);
    const { study } = current;
    const { ChipCell, ActiveInactiveCell, TooltipCell } = useGridCustomCell();
    const { t } = useTranslation('ram', { keyPrefix: 'manageCmControls' });
    const { t: tRisklog } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tCommon } = useTranslation('common');
    const chips = useMemo(() => statusRAGChips(tRisklog), []);
    const [panels, setPanels] = useState<IExtendedControlsHubPanelsAssociatedToRisk[]>([]);
    const [showUnassociatedRecords, setShowUnassociatedRecords] = useState<boolean>(false);
    const showUnassignCmControlDialog = useBoolean(false);

    const StudyHasCM2 = study && getIsActiveField('CM2', study.modules!);
    const HasmanageCmControlsPermission =
        StudyHasCM2 &&
        user?.can?.({
            module: Constants.Modules.RAM,
            permission: Permissions.Enabled,
            element: Constants.PageElements.CMControls,
        });

    const {
        data: panelsWithAssociatedRisks,
        isLoading,
        mutate,
    } = ControlsHubApi.useGetActiveOrAssociatedPanels(study?.id, HasmanageCmControlsPermission);

    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const handleAddClick = async () => {
        setFilterState({
            logic: 'and',
            filters: [],
        });
    };

    useEffect(() => {
        const subscribeDefault = RiskAssessmentService.onRefreshCmControlChartsGrid().subscribe((refreshData) => {
            if (refreshData) {
                mutate().catch((err) => window.console.log(err));
            }
        });

        return () => subscribeDefault.unsubscribe();
    }, []);

    useEffect(() => {
        if (panelsWithAssociatedRisks) {
            setPanels(
                orderBy(
                    (showUnassociatedRecords
                        ? panelsWithAssociatedRisks
                        : panelsWithAssociatedRisks.filter(
                              (panel) => panel.associatedRisks && panel.associatedRisks?.length > 0,
                          )
                    ).map((item) => {
                        return {
                            ...item,
                            plotTitle: {
                                active: item.active,
                                name: item.name,
                            },
                            noAssociatedRisks: item.associatedRisks?.length ?? 0,
                        };
                    }),
                    [{ field: 'plotTitle.name', dir: 'asc' }],
                ),
            );
        }
    }, [panelsWithAssociatedRisks, showUnassociatedRecords]);

    useEffect(() => {
        if (current.selectionState.selectedItem) {
            const control = panels.find((item) => item.id === current.selectionState.selectedItem?.id);
            setSelectionState((old) => ({
                ...old,
                selectedItem: control,
            }));
        }
    }, [panels]);

    const onSubmitUnassignRisks = (result: string) => {
        if (result === 'success') {
            mutate().catch((err) => window.console.log(err));
            ShowSuccess(tCommon('actionsSuccessfulToast'));
        } else ShowError(tCommon('errorMessage'));

        showUnassignCmControlDialog.setFalse();
    };

    const columns: GridColumnProps[] = [
        {
            id: 'plotTitle',
            field: 'plotTitle',
            title: t('grids.chartsGrid.columns.plotTitle'),
            lockedByDefault: true,
            minWidth: 250,
            filter: 'text',
            filterKey: 'name',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            minWidth: 80,
            id: 'noAssociatedRisks',
            field: 'noAssociatedRisks',
            title: t('grids.chartsGrid.columns.noAssociatedRisks'),
            filter: 'numeric',
            lockedByDefault: true,
        },
        {
            id: 'dataSet',
            field: 'dataSet',
            title: t('grids.chartsGrid.columns.dataset'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'timePeriod',
            field: 'timePeriod',
            title: t('grids.chartsGrid.columns.timePeriod'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            minWidth: 150,
            id: 'status',
            field: 'status',
            title: t('grids.chartsGrid.columns.status'),
            lovData: chips.map((c) => c.value),
            cells: { data: ChipCell(chips, { width: 95 }) },
        },
        {
            minWidth: 250,
            id: 'yAxisLabel',
            field: 'yAxisLabel',
            title: t('grids.chartsGrid.columns.yAxisLabel'),
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            minWidth: 150,
            id: 'ootValue',
            field: 'ootValue',
            title: t('grids.chartsGrid.columns.ootValue'),
            filter: 'numeric',
        },
        {
            id: 'toleranceLimits',
            field: 'toleranceLimits',
            sortable: false,
            title: t('grids.chartsGrid.columns.toleranceLimits'),
            children: [
                {
                    minWidth: 100,
                    id: 'benchmark',
                    field: 'benchmark',
                    title: t('grids.chartsGrid.columns.benchmark'),
                    filter: 'numeric',
                },
                {
                    minWidth: 100,
                    id: 'upperRed',
                    field: 'upperRed',
                    title: t('grids.chartsGrid.columns.upperRed'),
                    filter: 'numeric',
                },
                {
                    minWidth: 100,
                    id: 'upperAmber',
                    field: 'upperAmber',
                    title: t('grids.chartsGrid.columns.upperAmber'),
                    filter: 'numeric',
                },
                {
                    minWidth: 100,
                    id: 'lowerAmber',
                    field: 'lowerAmber',
                    title: t('grids.chartsGrid.columns.lowerAmber'),
                    filter: 'numeric',
                },
                {
                    minWidth: 100,
                    id: 'lowerRed',
                    field: 'lowerRed',
                    title: t('grids.chartsGrid.columns.lowerRed'),
                    filter: 'numeric',
                },
            ],
        },
        {
            minWidth: 100,
            id: 'id',
            field: 'id',
            title: 'ID',
            showByDefault: false,
        },
    ];

    const exportFileName = `${t('grids.chartsGrid.exportFileName')}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
        'DD-MMM-YYYY',
    )}`;

    const customGridMenuItems: GridMenuItemProps[] = [
        {
            title: showUnassociatedRecords ? tCommon('hideUnassociatedRecords') : tCommon('displayUnassociatedRecords'),
            'data-testid': 'cm-controls-charts-grid-show-unassociate-records',
            customIcon: <ToggleAssociatedRecordsIcon showUnassociated={showUnassociatedRecords} />,
            onClick: () => {
                setShowUnassociatedRecords((prevState) => !prevState);
            },
            ignoreReadOnlyContext: true,
        },
    ];

    return (
        <div style={{ height: '100%' }} className="remove-card-padding">
            <Grid
                customGridMenuItems={customGridMenuItems}
                excludeHiddenRowMenuActions
                title={t('grids.chartsGrid.title')}
                maxHeight="100%"
                testDataId="ra-manage-cm-controls-grid"
                data={panels}
                columns={columns}
                filterable
                showAddIcon={false}
                addCallback={handleAddClick}
                useLoadingOverlay
                canEdit={canEdit}
                loading={isLoading}
                scrollable="virtual"
                dataItemKey="id"
                exportFilename={exportFileName}
                selectable={{ enabled: true, mode: 'single' }}
                onSelectionChange={(e: GridSelectionChangeEvent) => {
                    const record = e.startDataItem as IExtendedControlsHubPanelsAssociatedToRisk;
                    if (!record) {
                        return;
                    }
                    setSelectionState((old) => ({
                        ...old,
                        selectedItem: record,
                    }));
                }}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                removeCardBorder
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grids.chartsGrid.actions.chartDetails'),
                        disabled: !canView,
                        icon: ['far', 'arrow-up-right-from-square'],
                        onClick: (dataItem) => {
                            const chart = dataItem as ControlsHubPanelsAssociatedToRisk;
                            const chartIdRef = chart.panelRef;
                            if (chartIdRef) {
                                const chartViewRoute = AllPaths.path('ra-risk-log-cmcontrol-chart-popout', {
                                    studyId: study?.id,
                                    chartId: chartIdRef,
                                });
                                PopOutPage(user?.organisationKey as string, chartViewRoute);
                            }
                        },
                    },
                    {
                        id: 2,
                        label: t('grids.chartsGrid.actions.unAssign'),
                        onClick: (dataItem) => {
                            const control = dataItem as IExtendedControlsHubPanelsAssociatedToRisk;
                            setSelectionState((old) => ({
                                ...old,
                                selectedItem: control,
                            }));
                            showUnassignCmControlDialog.setTrue();
                        },
                        icon: ['fal', 'link-slash'] as IconProp,
                        disabled: !canEdit,
                    },
                ]}
                filter={filterState}
                persistentStorageKey={String(PersistentStorageKeys['cm-controls-control-hub-chart'])}
            />
            {showUnassignCmControlDialog.value && current?.selectionState?.selectedItem && (
                <UnassignCmControlRiskDialog
                    onSubmit={onSubmitUnassignRisks}
                    onClose={() => showUnassignCmControlDialog.setFalse()}
                    panel={{
                        panelRef: current?.selectionState.selectedItem.panelRef,
                        associatedRisks: current?.selectionState.selectedItem.associatedRisks,
                        name: current?.selectionState.selectedItem.name,
                    }}
                    risk={undefined}
                    removeAllRisks
                    studyId={study?.id}
                />
            )}
        </div>
    );
};

export default CmControlChartsGrid;
